import * as THREE from 'three';
import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomFade, RoomModule, RoomModuleDefinition, RoomTick } from '@/game/roomModule';
import type { RoomDiagnostics } from '@/game/roomDiagnostics';
import { getAsset } from '@/models/registry';
import { DAWN_TRANSPORT_CUES, DAWN_TRANSPORT_FLAGS } from '@/story/act3/dawnTransport';
import type { HallSolid } from '@/world/solids';
import { collapseRigged } from '@/world/instancing';
import { findWorkerParts, workerAsset, type WorkerParts } from '@/models/env/worker';
import { bakeYardDawnGrids, yardCover } from '@/world/loadingYardStealth';
import { DRIVER_POST, LOADER_POST, YardDawnCrew } from '@/world/yardDawnCrew';
import type { LightGrid, StealthSnapshot } from '@/systems/stealth';
import type { SpawnPoint } from '@/physics/character';
import { HollyRig } from '@/anim/hollyRig';
import { ease } from '@/anim/types';
import { HollyPresenter, drainHollyBellQueue } from '@/game/hollyPresenter';
import { hollyAsset } from '@/models/holly';
import type { CompanionBellEvent } from '@/systems/companionBell';
import { DAWN_LEAF_OPEN_ID, DAWN_LEAF_SHUT_ID, DawnHolly, HOLLY_DAWN_LIE_UP } from '@/story/act3/dawnHolly';
import { DawnHerd } from '@/story/act3/dawnHerd';
import { DawnHerdView } from '@/world/dawnHerd';
import {
  createLoadingYard, loadingYardSolids, YARD_DAWN_JUNCTION_BOX, YARD_DAWN_JUNCTION_BOX_INTERACT, YARD_DAWN_SPAWN,
  DAWN_HERD_GATE_DISTANCE, DAWN_HERD_LANE_LENGTH, projectOntoDawnHerdLane, RACE_GATE_OPEN_YAW, raceGateLeafSolid, YARD_RAMP_AT, yardDawnLighting, yardDawnZone,
} from '@/world/loadingYard';

/**
 * Beat 12's yard: beat 6's room at dawn, as a module (`docs/beat-12-plan.md`).
 *
 * **A separate room id, not a branch in beat 6's.** The storm yard is a legacy
 * branch in `main.ts` with a director, a stealth system and weather wired
 * through it; putting the dawn behind a flag there would have grown the file
 * this project is trying to empty, and would have put beat 6's census at the
 * mercy of every beat-12 edit. The geometry is shared — `createLoadingYard`
 * and `loadingYardSolids` take `{ dawn: true }` — so this is the same room at
 * another hour and not a copy of it (S-9: the yard is not modelled twice).
 *
 * The room owns the yard's STATE — whether the lights are out, where the ramp
 * motor is — and raises it as flags; the beat part owns the story and her map.
 * The crew (stage 3), Holly (4) and the herd (5) belong here too, beside the
 * pivots they turn: `ramp` and `raceGateLeaf` are the live handles.
 */

export const DAWN_JUNCTION_BOX_ID = 'dawn-junction-box';

/** The two fixtures mark 1 cuts. `yardDawnLighting` keeps exactly these. */
const CUT_FIXTURES = ['race-flood', 'shed-door-lamp'] as const;

/**
 * Seconds from the nudge to the dark: beat 8's throw, the same box and the
 * same lever travel, so the gesture reads as the one she has made before.
 */
const THROW_SECONDS = 1.2;

/** Presentation time is clamped per frame, as the office's is: a hidden tab must not skip the throw. */
const MAX_FRAME = 0.1;

/**
 * Capture's two halves, in SIMULATION seconds (D-045: a fixed-step decision is
 * never taken on the render-rate fade value — beat 6's director records the
 * review that found it doing so). Black by the end of the first; her body is
 * moved at that instant; the second brings the yard back.
 */
const CAPTURE_OUT = 0.8;
const CAPTURE_IN = 0.8;

/** The floods relight over this, in render time: a breaker reset, not a switch. */
const RELIGHT_SECONDS = 0.6;

/**
 * The box as something she bumps into. It is drawn by the asset this module
 * places, so it is added here rather than listed in `loadingYardSolids()`,
 * which is a list of things that are both (the office's Kestner legs are the
 * precedent).
 */
function junctionBoxSolid(): HallSolid {
  const footprint = getAsset('fuse-box')?.footprint;
  if (footprint === undefined) throw new Error('yard-dawn: fuse-box asset is not registered');
  return {
    name: 'dawn-junction-box-solid',
    half: { x: footprint.width / 2, y: footprint.height / 2, z: footprint.length / 2 },
    pos: { x: YARD_DAWN_JUNCTION_BOX.x, y: footprint.height / 2, z: YARD_DAWN_JUNCTION_BOX.z },
    colour: 0,
    kind: 'metal',
  };
}

export function createYardDawnRoomModule(
  options: { part: number; dressing: boolean; out?: number },
  capabilities: RoomCapabilities,
): RoomModule {
  const room = createLoadingYard({ dawn: true, paddock: options.dressing });
  capabilities.scene.add(room.group);
  const colliders: ColliderHandle[] = loadingYardSolids({ dawn: true }).map((solid) => capabilities.colliders.add(solid));

  // Not instanced: the lid and lever are live pivots, and merging them would
  // leave a story that goes dark with nothing on the wall moving (the blackout
  // hall's own note on the same asset).
  const box = getAsset('fuse-box')?.create();
  if (box === undefined) throw new Error('yard-dawn: fuse-box asset is not registered');
  box.name = 'dawn-junction-box';
  box.position.set(YARD_DAWN_JUNCTION_BOX.x, YARD_DAWN_JUNCTION_BOX.y, YARD_DAWN_JUNCTION_BOX.z);
  const lid = box.getObjectByName('fuse-box-lid-pivot');
  const lever = box.getObjectByName('fuse-box-lever-pivot');
  if (lid === undefined || lever === undefined) throw new Error('yard-dawn: fuse box lost its live pivots');
  // As placed raw it cost 12 draw calls of the 23 stages 3–5 have to spend
  // (`docs/performance.md`): seven meshes in three passes. Merged about its two
  // pivots, and without a shadow — a 35 cm box flat on a wall casts none that
  // reads — it is the pivots' batches in two passes.
  collapseRigged(box, [lid, lever]);
  box.traverse((object) => { object.castShadow = false; });
  capabilities.scene.add(box);
  colliders.push(capabilities.colliders.add(junctionBoxSolid()));

  /**
   * Mark 1, as SIMULATION state: set on the tick of the nudge (or by the
   * plan's backstop) and never unset. Capture does not relight the yard (the
   * plan: *"marks already done stay done"*). The light grid reads this, not the
   * lamps' intensities, which are presentation.
   */
  let lightsOut = false;
  /** 0..1 on the render step: the lever's travel and the lamps' fade. */
  let throwProgress = 0;
  /** 0..1 on the render step once the driver has reset the box. */
  let relightProgress = 0;
  let disposed = false;

  // ── The crew and what they can see ─────────────────────────────────────────
  const crew = new YardDawnCrew();
  const grids = bakeYardDawnGrids();
  // The stealth system holds THIS object; what it samples is switched inside
  // it, off simulation state, so the swap needs no host cooperation.
  const grid: LightGrid = {
    ...grids.lit,
    sample: (x, z) => (lightsOut && !crew.floodsBack ? grids.dark : grids.lit).sample(x, z),
  };

  // Drawn whatever `&dress=0` says: they are the watchers, not dressing, and
  // gating them on it put the crew inside the census's paddock delta.
  const figures = [DRIVER_POST, LOADER_POST].map((post) => {
    const mesh = workerAsset.create();
    mesh.position.set(post.x, 0, post.z);
    capabilities.scene.add(mesh);
    const last: { x: number; z: number } = { x: post.x, z: post.z };
    return { mesh, parts: findWorkerParts(mesh), travelled: 0, last };
  });

  // ── Holly, mark 2 (stage 4) ──────────────────────────────────────────────
  // The leaf is shut and SOLID from the start — the race's mouth had no
  // collider in beat 6, and at dawn it is what holds the herd in. It leaves on
  // the tick her rear starts and the open leaf arrives when the swing ends:
  // in between the leaf is in motion and stops nothing (the wool shed's door).
  const holly = new DawnHolly();
  const hollyRoot = hollyAsset.create();
  hollyRoot.position.set(HOLLY_DAWN_LIE_UP.x, 0, HOLLY_DAWN_LIE_UP.z);
  hollyRoot.rotation.y = HOLLY_DAWN_LIE_UP.yaw;
  capabilities.scene.add(hollyRoot);
  const hollyPresenter = new HollyPresenter(hollyRoot, new HollyRig(hollyRoot));
  const bellQueue: CompanionBellEvent[] = [];
  let leafShut: ColliderHandle | null = capabilities.colliders.add(raceGateLeafSolid(0, DAWN_LEAF_SHUT_ID));
  let leafOpen: ColliderHandle | null = null;
  /** Copied out of the borrowed tick, for `callVerb` on the render step. */
  const her: { x: number; z: number } = { x: YARD_DAWN_SPAWN.x, z: YARD_DAWN_SPAWN.z };

  // ── The herd, mark 3 (stage 5) ─────────────────────────────────────────────
  // Forty pigs as one scalar each on an authored lane (`dawnHerd.ts`). The
  // throat opens only with the leaf HELD and the floods OUT (the plan: *"the
  // herd cannot leave the race while the gate is shut, and the crew will not
  // let it while the floods are on"*). Drawn whatever `&dress=0` says: it is
  // the beat, not dressing.
  const herd = new DawnHerd({ laneLength: DAWN_HERD_LANE_LENGTH, gateDistance: DAWN_HERD_GATE_DISTANCE });
  const herdView = new DawnHerdView();
  capabilities.scene.add(herdView.group);
  /** Her position on the previous tick, for her ground speed: a roll pulls the string (the plan). */
  const lastHer: { x: number; z: number } = { x: YARD_DAWN_SPAWN.x, z: YARD_DAWN_SPAWN.z };
  let released = false;

  // ── Capture (spec §5.6, D-084): fade, back to the cull door, the alarm one
  // higher (the stealth system raises it on the detection edge), and the
  // sequencer untouched. The crew keeps working through it — the clock is the
  // whole cost. Live from the start; dropped when the beat takes the frame.
  let fade: RoomFade | null = null;
  let captureLive = true;
  let captureStage: 'out' | 'in' | null = null;
  let captureElapsed = 0;
  let captureMove: ((to: SpawnPoint) => void) | null = null;
  let captures = 0;
  const wasDetected = new Set<string>();
  /** The last thing the crew made of her, for diagnostics: suspicion and visibility per man. */
  let watched: { id: string; suspicion: number; visibility: number; state: string }[] = [];

  const cutLights = (animated: boolean): void => {
    if (lightsOut) return;
    lightsOut = true;
    relightProgress = 0;
    crew.send();
    capabilities.interaction.setEnabled(DAWN_JUNCTION_BOX_ID, false);
    // The pump IS the ramp's motor: cutting it is the jam, and the throw's
    // own sound carries the last of the hum into silence (beat 8's fuse).
    capabilities.cutAmbience();
    if (animated) capabilities.playOneShot('fuse-throw', { gain: 0.72, position: YARD_DAWN_JUNCTION_BOX_INTERACT });
    else throwProgress = 1;
  };

  /** The leaf's collider follows her clock on the fixed step, and mark 2 is raised off it. */
  const stepLeaf = (tick: RoomTick): void => {
    const { leafProgress } = holly.snapshot();
    if (leafProgress > 0 && leafShut !== null) {
      capabilities.colliders.remove(leafShut);
      leafShut = null;
    }
    if (leafProgress >= 1) {
      leafOpen ??= capabilities.colliders.add(raceGateLeafSolid(RACE_GATE_OPEN_YAW, DAWN_LEAF_OPEN_ID));
      tick.raiseFlag(DAWN_TRANSPORT_FLAGS.gateHeld);
    }
  };

  const poseFigure = (index: number, x: number, z: number, facing: number, walking: boolean, frameDt: number, stride: number): void => {
    const figure = figures[index];
    if (figure === undefined) return;
    const moved = Math.hypot(x - figure.last.x, z - figure.last.z);
    figure.last = { x, z };
    if (walking) figure.travelled += moved;
    figure.mesh.position.set(x, 0, z);
    figure.mesh.rotation.y = facing;
    // Beat 4's gait, on distance, so a man who stops stops swinging.
    const swing = walking && moved > 0.00001 ? Math.sin((figure.travelled / stride) * Math.PI) : 0;
    const settle = 1 - Math.exp(-8 * frameDt);
    const pose = (node: THREE.Object3D, target: number): void => { node.rotation.x += (target - node.rotation.x) * settle; };
    const parts: WorkerParts = figure.parts;
    pose(parts.hips[0], swing * 0.42);
    pose(parts.hips[1], -swing * 0.42);
    pose(parts.shoulders[0], -swing * 0.26);
    pose(parts.shoulders[1], swing * 0.26);
  };

  return {
    interactables: [{
      id: DAWN_JUNCTION_BOX_ID,
      at: { ...YARD_DAWN_JUNCTION_BOX_INTERACT },
      verbs: ['nudge'] as const,
      // Beat 8's fuse reach: the same box at the same height, so the same stand.
      reach: 1.15,
    }],
    stealth: {
      grid,
      cover: yardCover({ dawn: true }),
      watchers: crew.watchers,
      // The apron is dry at dawn (D-202); concrete is the foley table's word for it.
      footfallSurface: 'concrete',
      afterStep(snapshot: StealthSnapshot, moveSelf): void {
        watched = snapshot.watchers.map((watcher) => ({
          id: watcher.watcherId, suspicion: watcher.suspicion, visibility: watcher.lastVisibility.visibility, state: watcher.state,
        }));
        const detected = snapshot.watchers.filter((watcher) => watcher.state === 'detected').map((watcher) => watcher.watcherId);
        // On the EDGE of a detection, not its level: suspicion takes seconds to
        // drain, and a level test would catch her again at the cull door for a
        // sighting at the race.
        const fresh = detected.some((id) => !wasDetected.has(id));
        wasDetected.clear();
        for (const id of detected) wasDetected.add(id);
        if (!fresh || !captureLive || captureStage !== null) return;
        captures += 1;
        captureStage = 'out';
        captureElapsed = 0;
        captureMove = moveSelf;
        fade ??= capabilities.createFade();
        fade.setTarget(1);
      },
    },
    fixedStep(tick): void {
      if (!lightsOut && tick.nudged(DAWN_JUNCTION_BOX_ID)) cutLights(true);
      // Every tick, not once: a flag raised from `handleCue` would have no
      // tick to raise it on, and the story set is idempotent.
      if (lightsOut) tick.raiseFlag(DAWN_TRANSPORT_FLAGS.lightsOut);

      const driver = crew.step(tick.dt);
      if (driver.footfall) {
        capabilities.playOneShot('footfall-concrete', { gain: 0.85, position: { x: driver.x, y: 0, z: driver.z }, detune: -1100 });
      }
      // Back before a single pig was out: the plan is not over, the box is
      // live again (independent review, session 67 — the stage used to close
      // on a herd that had never been released). Once the herd is moving,
      // the floods returning is the close the plan describes.
      if (crew.floodsBack && lightsOut && herd.snapshot().throughs === 0) {
        lightsOut = false;
        throwProgress = 0;
        crew.rearm();
        capabilities.interaction.setEnabled(DAWN_JUNCTION_BOX_ID, true);
      }
      if (crew.floodsBack && lightsOut) tick.raiseFlag(DAWN_TRANSPORT_FLAGS.floodsBack);

      her.x = tick.player.x;
      her.z = tick.player.z;
      // Her bell is voiced and never fed to the crew's stealth: a sheep's bell
      // in the paddock at dawn is the farm's own sound, and a capture caused by
      // the companion would be a punishment the player did not choose.
      for (const event of holly.step(tick.dt, her, tick.called)) bellQueue.push(event);
      stepLeaf(tick);

      released = lightsOut && !crew.floodsBack && holly.snapshot().leafProgress >= 1;
      const leadSpeed = tick.dt > 0 ? Math.hypot(her.x - lastHer.x, her.z - lastHer.z) / tick.dt : 0;
      lastHer.x = her.x;
      lastHer.z = her.z;
      herd.step(tick.dt, { released, lead: projectOntoDawnHerdLane(her), leadSpeed });
      if (herd.snapshot().atVehicleGate) tick.raiseFlag(DAWN_TRANSPORT_FLAGS.herdAtGate);

      if (captureStage !== null) {
        captureElapsed += tick.dt;
        if (captureStage === 'out' && captureElapsed >= CAPTURE_OUT) {
          captureMove?.(YARD_DAWN_SPAWN);
          captureMove = null;
          captureStage = 'in';
          fade?.setTarget(0);
        } else if (captureStage === 'in' && captureElapsed >= CAPTURE_OUT + CAPTURE_IN) {
          captureStage = null;
        }
      }
    },
    get callVerb(): 'call' | null {
      return holly.canCall(her) ? 'call' : null;
    },
    handleCue(cue): void {
      if (cue.id === DAWN_TRANSPORT_CUES.applyPlan) {
        cutLights(false);
        holly.applyHeld();
      }
      // The beat's own fades own the frame; capture waits for the yard to be back.
      if (cue.id === DAWN_TRANSPORT_CUES.hold || cue.id === DAWN_TRANSPORT_CUES.fadeOut) {
        captureLive = false;
        // A capture still fading OUT is abandoned, not finished: finishing it
        // moved her to the cull door inside the held close (review, session 67).
        if (captureStage === 'out') {
          captureStage = null;
          captureMove = null;
          fade?.setTarget(0);
        }
      }
      if (cue.id === DAWN_TRANSPORT_CUES.open) captureLive = true;
    },
    renderStep(frameDt): void {
      if (disposed) return;
      const dt = Math.min(Math.max(0, frameDt), MAX_FRAME);
      fade?.update(dt);
      const driver = crew.driver.state;
      poseFigure(0, driver.x, driver.z, crew.driverWatcher.facing, driver.phase === 'walking' || driver.phase === 'returning', dt, 0.72);
      poseFigure(1, LOADER_POST.x, LOADER_POST.z, crew.loaderWatcher.facing, false, dt, 0.72);
      if (bellQueue.length > 0) drainHollyBellQueue(bellQueue, capabilities.playOneShot);
      herdView.update(dt, herd.snapshot());
      const hollyState = holly.snapshot();
      // Her rear and the leaf read one clock; the eased curve changes how it
      // travels, never when it arrives (the wool shed's door note).
      room.raceGateLeaf.rotation.y = RACE_GATE_OPEN_YAW * ease.inOut(hollyState.leafProgress);
      hollyPresenter.update(frameDt, hollyState.holly, { latchProgress: hollyState.leafProgress, doorProgress: 0, riseProgress: 0 }, null);
      if (crew.floodsBack && relightProgress < 1) {
        relightProgress = Math.min(1, relightProgress + dt / RELIGHT_SECONDS);
        for (const name of CUT_FIXTURES) capabilities.lights.scale(name, relightProgress);
        return;
      }
      if (!lightsOut) {
        // Re-armed: the lever is back up for the next throw.
        lid.rotation.y = 0;
        lever.rotation.z = 0;
        return;
      }
      if (throwProgress > 1) return;
      throwProgress = Math.min(1, throwProgress + dt / THROW_SECONDS);
      lid.rotation.y = -throwProgress * 1.05;
      lever.rotation.z = -throwProgress * 0.8;
      for (const name of CUT_FIXTURES) capabilities.lights.scale(name, 1 - throwProgress);
      // Parked past 1 once applied, so the lamps are not rescaled every frame forever.
      if (throwProgress >= 1) throwProgress = 1.0001;
    },
    framing: () => null,
    diagnostics: (): RoomDiagnostics => ({
      kind: 'yard-dawn',
      yardDawn: {
        rampPitch: room.ramp.rotation.x,
        raceGateYaw: room.raceGateLeaf.rotation.y,
        lightsOut,
        rampMotor: lightsOut ? 'jammed' : 'running',
        throwProgress: Math.min(1, throwProgress),
        crew: crew.phase,
        floodsBack: crew.floodsBack,
        resetRemaining: crew.resetRemaining,
        driver: { x: crew.driver.state.x, z: crew.driver.state.z },
        captures,
        capturing: captureStage !== null,
        holly: (() => {
          const state = holly.snapshot();
          return { x: state.holly.x, z: state.holly.z, yaw: state.holly.yaw, phase: state.phase, leafProgress: state.leafProgress };
        })(),
        watched,
        herd: (() => {
          const state = herd.snapshot();
          return { released, throughs: state.throughs, headDistance: state.headDistance, atVehicleGate: state.atVehicleGate };
        })(),
        leafSolid: leafOpen !== null ? 'open' : leafShut !== null ? 'shut' : 'none',
      },
    }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const collider of colliders) capabilities.colliders.remove(collider);
      if (leafShut !== null) capabilities.colliders.remove(leafShut);
      if (leafOpen !== null) capabilities.colliders.remove(leafOpen);
      capabilities.scene.remove(hollyRoot);
      capabilities.scene.remove(herdView.group);
      herdView.dispose();
      capabilities.scene.remove(room.group);
      capabilities.scene.remove(box);
      for (const figure of figures) capabilities.scene.remove(figure.mesh);
      fade?.dispose();
    },
  };
}

/**
 * The storm, the rain and the lightning are off: none of them is wired here,
 * and they were never the room's — they are beat 6's director. The sky is
 * baked from the dawn palette (`yardDawnZone`'s doc says how it reads), with a
 * seed of its own so the dawn's strata are not the storm's clouds relit.
 */
function yardDawnEnvironment(): RoomEnvironment {
  const zone = yardDawnZone();
  return {
    zone,
    lighting: yardDawnLighting(),
    // Zenith from `fill`, the zone's sky colour, not its `shadow` (the storm's
    // night blue); the horizon line from `fog`, so the hazed field dissolves
    // into a warm band instead of stopping at a ruled edge (`SkyOptions.middle`).
    background: { kind: 'sky', seed: 12, zenith: zone.fill, middle: zone.fog },
    spawn: YARD_DAWN_SPAWN,
    // The ramp's motor, at its hinge. The yard's one machine at dawn and the
    // sound mark 1 stops: a pump's pulse, which is what a hydraulic ramp
    // holding its angle sounds like. The pump is the right loop because the
    // throw's own sound (`fuse-throw`) ends on the tail of a hum.
    ambience: [{ id: 'slurry-pump', at: { x: YARD_RAMP_AT.x + 1.6, y: 0.4, z: YARD_RAMP_AT.z }, gain: 0.45 }],
  };
}

export const YARD_DAWN_ROOM_MODULE: RoomModuleDefinition = {
  room: 'yard-dawn',
  // Beat 6's yard at dawn, at beat 6's origin: her storm-night walk from the
  // bins to the trailer is on the sheet she plans from.
  ledgerSheet: 'yard',
  environment: yardDawnEnvironment,
  create: createYardDawnRoomModule,
};

/** Exported for specs that measure the dark against the lit yard. */
export const YARD_DAWN_CUT_FIXTURES: readonly string[] = CUT_FIXTURES;
